import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, ListChecks, Paperclip, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const DC_EPL = "https://ocp.dc.gov/page/excluded-parties-list";
const resultClass = (result) => ({ "No match": "compliance-result-clear", "Possible match": "compliance-result-pending", Confirmed: "compliance-result-match" }[result] || "");

const EvidenceCell = ({ review, row, canEdit, onChanged }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function upload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      await api.post(`/compliance/exclusions/dc-epl/${review.id}/rows/${row.person_id}/evidence`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setError("");
      await onChanged();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }
  return (
    <div className="dc-evidence" data-testid={`dc-evidence-${row.person_id}`}>
      {(row.evidence || []).map((file) => (
        <a key={file.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/exclusions/dc-epl/${review.id}/rows/${row.person_id}/evidence/${file.id}`} target="_blank" rel="noreferrer" data-testid={`dc-evidence-file-${file.id}`}><Paperclip size={11} /> {file.filename}</a>
      ))}
      {canEdit && review.status === "Open" && (
        <label className="dc-evidence-add">
          <input ref={input} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid={`dc-evidence-input-${row.person_id}`} onChange={upload} disabled={busy} />
          <span>{busy ? "Uploading…" : row.evidence?.length ? "Add another" : "Attach search result"}</span>
        </label>
      )}
      {error && <small className="form-message error-message">{error}</small>}
    </div>
  );
};

const ResultCell = ({ review, row, canEdit, onChanged }) => {
  const [result, setResult] = useState(row.result === "Pending" ? "No match" : row.result);
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function save() {
    setBusy(true);
    try {
      await api.put(`/compliance/exclusions/dc-epl/${review.id}/rows/${row.person_id}`, { result, note });
      setError("");
      await onChanged();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }
  if (!canEdit) return <em className={`compliance-result ${resultClass(row.result)}`} data-testid={`dc-result-${row.person_id}`}>{row.result}</em>;
  return (
    <div className="dc-result-form" data-testid={`dc-result-form-${row.person_id}`}>
      <select value={result} data-testid={`dc-result-select-${row.person_id}`} onChange={(e) => setResult(e.target.value)}>
        <option>No match</option><option>Possible match</option><option>Confirmed</option>
      </select>
      <input value={note} placeholder={result === "No match" ? "Note (optional)" : "Describe the record that matched"} maxLength={600} data-testid={`dc-result-note-${row.person_id}`} onChange={(e) => setNote(e.target.value)} />
      <button type="button" className="button button-ghost" data-testid={`dc-result-save-${row.person_id}`} disabled={busy || !row.evidence?.length} title={row.evidence?.length ? "" : "Attach the search result first"} onClick={save}><Save size={13} /> {row.result === "Pending" ? "Record" : "Update"}</button>
      {error && <small className="form-message error-message" data-testid={`dc-result-error-${row.person_id}`}>{error}</small>}
    </div>
  );
};

export const DcEplChecklist = ({ canEdit, onChanged }) => {
  const [review, setReview] = useState(null);
  const [month, setMonth] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const response = await api.get("/compliance/exclusions/dc-epl", { params: month ? { month } : {} });
      setReview(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [month]);
  useEffect(() => { load(); }, [load]);
  async function changed() {
    await load();
    await onChanged?.();
  }
  if (error) return <section className="compliance-card" id="dc-epl"><p className="form-message error-message">{error}</p></section>;
  if (!review) return null;
  return (
    <section className="compliance-card" id="dc-epl" data-testid="dc-epl-checklist">
      <div className="section-heading">
        <div>
          <h2><ListChecks size={17} /> DC Excluded Parties List — {review.label}</h2>
          <p>
            The DC list is search-only. Search each active person at <a className="dc-epl-link" href={DC_EPL} target="_blank" rel="noreferrer">ocp.dc.gov <ExternalLink size={11} /></a>, attach the result (screenshot or PDF), then record No match, Possible match or Confirmed. A possible match blocks new assignments until HR clears it; a confirmed match removes the person from active assignments and alerts the CEO and HR.
          </p>
          <p className="pairing-meta" data-testid="dc-epl-progress">
            {review.status === "Complete" ? `Complete · all ${review.total} people reviewed · finished ${new Date(review.completed_at).toLocaleDateString()} by ${review.completed_by_name}` : `${review.total - review.pending} of ${review.total} people reviewed · due ${review.due_date}`}
            {review.overdue && <em className="compliance-result compliance-result-match" data-testid="dc-epl-overdue">Overdue</em>}
            {review.possible ? ` · ${review.possible} possible` : ""}{review.confirmed ? ` · ${review.confirmed} confirmed` : ""}
          </p>
        </div>
        {review.months?.length > 1 && (
          <select value={month || review.month} data-testid="dc-epl-month" onChange={(e) => setMonth(e.target.value)}>
            {review.months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        )}
      </div>
      <div className="credential-table-wrap exclusion-table-wrap">
        <table className="exclusion-table dc-epl-table">
          <thead><tr><th>Person</th><th>Search result (evidence)</th><th>Result</th><th>Reviewed</th></tr></thead>
          <tbody>
            {review.rows.map((row) => (
              <tr key={row.person_id} className={row.result === "Pending" ? "" : `is-${row.result.toLowerCase().replace(" ", "-")}`} data-testid={`dc-row-${row.person_id}`}>
                <td><b>{row.name}</b><span>{row.label}</span></td>
                <td><EvidenceCell review={review} row={row} canEdit={canEdit} onChanged={changed} /></td>
                <td>
                  <em className={`compliance-result ${resultClass(row.result)}`} data-testid={`dc-row-result-${row.person_id}`}>{row.result}</em>
                  {canEdit && review.status === "Open" && <ResultCell review={review} row={row} canEdit={canEdit} onChanged={changed} />}
                  {row.note && !(canEdit && review.status === "Open") && <small className="pairing-meta">{row.note}</small>}
                </td>
                <td>{row.reviewed_at ? <>{row.reviewed_by_name}<br /><small>{new Date(row.reviewed_at).toLocaleString()}</small></> : <span className="pairing-meta">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!review.rows.length && <p className="operations-empty-state">No active people to check.</p>}
      </div>
    </section>
  );
};
